import { createContext, useContext } from "react";

/**
 * Cross-cutting facts every widget primitive needs, subscribed to once.
 *
 * `prefers-reduced-motion` was being read by each primitive through its own
 * `useMediaQuery`, which registers a `matchMedia` listener per *instance* — a
 * full canvas runs six metric figures, a ring, two spark series and two progress
 * tracks, so a preference that changes approximately never had a dozen live
 * subscriptions watching it. One subscription at the canvas root, handed down.
 *
 * Defaults to animations enabled rather than throwing when no provider is
 * present: a primitive rendered outside a canvas (a Storybook page, a test, a
 * future reuse elsewhere) should render, not crash. Motion is the safe default
 * because the CSS side of every animation is *also* guarded by a
 * `prefers-reduced-motion` media query — this flag only suppresses the
 * JS-driven ones, so a missing provider degrades to "CSS still respects the
 * preference", never to "the preference is ignored".
 */
export interface IWidgetEnvironment {
  animationsEnabled: boolean;
}

const FALLBACK: IWidgetEnvironment = { animationsEnabled: true };

export const WidgetEnvironmentContext =
  createContext<IWidgetEnvironment>(FALLBACK);

export function useWidgetEnvironment(): IWidgetEnvironment {
  return useContext(WidgetEnvironmentContext);
}
